"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { getAppMetadata, type AppMetadata } from "./app-metadata";
interface AppStat {
  source: string;
  totalTokens: number;
  totalRequests: number;
  lastUsedAt: string | null;
}
interface AppsGridProps {
  apps: AppStat[];
}
interface RankedApp extends AppStat {
  rank: number;
  meta: AppMetadata;
}
const CATEGORIES: Array<{
  value: AppMetadata["category"] | "all";
  labelKey: string;
}> = [{
  value: "all",
  labelKey: "public.apps.grid.categories.all"
}, {
  value: "coding",
  labelKey: "public.apps.grid.categories.coding"
}, {
  value: "automation",
  labelKey: "public.apps.grid.categories.automation"
}, {
  value: "other",
  labelKey: "public.apps.grid.categories.other"
}];
const numberFormatter = new Intl.NumberFormat("en-US");
function formatTokens(n: number): string {
  if (n >= 1_000_000_000) {
    return `${(n / 1_000_000_000).toFixed(2)}B`;
  }
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(2)}M`;
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(1)}K`;
  }
  return numberFormatter.format(n);
}
type TFunction = ReturnType<typeof useI18n>["t"];
function formatRelative(iso: string | null, t: TFunction): string | null {
  if (!iso) {
    return null;
  }
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) {
    return t("public.apps.grid.justNow");
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return t("public.apps.grid.minutesAgo", {
      value: minutes
    });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t("public.apps.grid.hoursAgo", {
      value: hours
    });
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return t("public.apps.grid.daysAgo", {
      value: days
    });
  }
  const months = Math.floor(days / 30);
  return t("public.apps.grid.monthsAgo", {
    value: months
  });
}
function AppLogo(t0) {
  const $ = _c(16);
  const {
    Icon,
    name,
    size: t1
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const dim = size === "lg" ? "h-14 w-14" : size === "sm" ? "h-9 w-9" : "h-12 w-12";
  const iconDim = size === "lg" ? "h-8 w-8" : size === "sm" ? "h-5 w-5" : "h-7 w-7";
  if (Icon) {
    let t2;
    if ($[0] !== dim) {
      t2 = cn("flex items-center justify-center rounded-xl bg-muted text-foreground border border-border/40", dim);
      $[0] = dim;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== Icon || $[3] !== iconDim) {
      t3 = <Icon className={iconDim} />;
      $[2] = Icon;
      $[3] = iconDim;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t2 || $[6] !== t3) {
      t4 = <div className={t2}>{t3}</div>;
      $[5] = t2;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t2;
  if ($[8] !== name) {
    t2 = name.replace(/^https?:\/\//, "").charAt(0).toUpperCase();
    $[8] = name;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const initial = t2;
  const t3 = size === "lg" ? "text-xl" : "text-base";
  let t4;
  if ($[10] !== dim || $[11] !== t3) {
    t4 = cn("flex items-center justify-center rounded-xl bg-muted border border-border/40 font-display font-semibold text-muted-foreground", dim, t3);
    $[10] = dim;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = initial || "?";
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function PodiumCard(t0) {
  const $ = _c(114);
  const {
    app,
    maxTokens,
    tone,
    t
  } = t0;
  const widthPct = maxTokens > 0 ? app.totalTokens / maxTokens * 100 : 0;
  let T0;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== app.lastUsedAt || $[1] !== app.meta.Icon || $[2] !== app.meta.description || $[3] !== app.meta.displayName || $[4] !== app.meta.url || $[5] !== app.rank || $[6] !== app.source || $[7] !== app.totalRequests || $[8] !== app.totalTokens || $[9] !== t || $[10] !== tone || $[11] !== widthPct) {
    const lastUsed = formatRelative(app.lastUsedAt, t);
    let t13;
    let t14;
    if ($[25] !== app.meta.Icon || $[26] !== app.meta.description || $[27] !== app.meta.displayName || $[28] !== app.meta.url || $[29] !== app.rank || $[30] !== app.source || $[31] !== app.totalTokens || $[32] !== t || $[33] !== tone) {
      const toneStyles = {
        gold: {
          ring: "ring-blue-500/40 dark:ring-blue-400/30",
          rankColor: "text-blue-500 dark:text-blue-400",
          glow: "bg-blue-500/[0.08] dark:bg-blue-500/[0.06]",
          bar: "bg-blue-500 dark:bg-blue-400"
        },
        silver: {
          ring: "ring-border",
          rankColor: "text-foreground",
          glow: "bg-foreground/[0.04]",
          bar: "bg-foreground"
        },
        bronze: {
          ring: "ring-border",
          rankColor: "text-foreground",
          glow: "bg-foreground/[0.03]",
          bar: "bg-foreground"
        }
      }[tone];
      const Wrap = app.meta.url ? "a" : "div";
      let t15;
      if ($[45] !== app.meta.url) {
        t15 = app.meta.url ? {
          href: app.meta.url,
          target: "_blank" as const,
          rel: "noopener noreferrer"
        } : {};
        $[45] = app.meta.url;
        $[46] = t15;
      } else {
        t15 = $[46];
      }
      const wrapProps = t15;
      T0 = Wrap;
      t10 = wrapProps;
      t11 = cn("group relative block overflow-hidden rounded-2xl border bg-card p-6 ring-1 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl", toneStyles.ring);
      t12 = <div aria-hidden={true} className={cn("pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl", toneStyles.glow)} />;
      t7 = "relative space-y-5";
      let t16;
      if ($[47] !== app.meta.Icon || $[48] !== app.meta.displayName) {
        t16 = <AppLogo Icon={app.meta.Icon} name={app.meta.displayName} size="lg" />;
        $[47] = app.meta.Icon;
        $[48] = app.meta.displayName;
        $[49] = t16;
      } else {
        t16 = $[49];
      }
      let t17;
      if ($[50] !== app.meta.displayName) {
        t17 = <h3 className="font-display text-xl font-bold tracking-tight">{app.meta.displayName}</h3>;
        $[50] = app.meta.displayName;
        $[51] = t17;
      } else {
        t17 = $[51];
      }
      let t18;
      if ($[52] !== app.source) {
        t18 = <p className="font-mono text-xs text-muted-foreground truncate">{app.source}</p>;
        $[52] = app.source;
        $[53] = t18;
      } else {
        t18 = $[53];
      }
      let t19;
      if ($[54] !== t17 || $[55] !== t18) {
        t19 = <div className="min-w-0">{t17}{t18}</div>;
        $[54] = t17;
        $[55] = t18;
        $[56] = t19;
      } else {
        t19 = $[56];
      }
      let t20;
      if ($[57] !== t16 || $[58] !== t19) {
        t20 = <div className="flex items-center gap-4">{t16}{t19}</div>;
        $[57] = t16;
        $[58] = t19;
        $[59] = t20;
      } else {
        t20 = $[59];
      }
      const t21 = cn("font-display text-5xl font-bold leading-none tracking-tighter tabular-nums", toneStyles.rankColor);
      const t22 = String(app.rank);
      let t23;
      if ($[60] !== t22) {
        t23 = t22.padStart(2, "0");
        $[60] = t22;
        $[61] = t23;
      } else {
        t23 = $[61];
      }
      let t24;
      if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = <ArrowUpRight className="mt-1 h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground" />;
        $[62] = t24;
      } else {
        t24 = $[62];
      }
      const t25 = <div className="flex flex-col items-end"><span className={t21}>{t23}</span>{t24}</div>;
      if ($[63] !== t20 || $[64] !== t25) {
        t8 = <div className="flex items-start justify-between gap-4">{t20}{t25}</div>;
        $[63] = t20;
        $[64] = t25;
        $[65] = t8;
      } else {
        t8 = $[65];
      }
      if ($[66] !== app.meta.description) {
        t9 = <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 min-h-[2.5rem]">{app.meta.description}</p>;
        $[66] = app.meta.description;
        $[67] = t9;
      } else {
        t9 = $[67];
      }
      t4 = "space-y-2 pt-2";
      let t26;
      if ($[68] !== app.totalTokens) {
        t26 = formatTokens(app.totalTokens);
        $[68] = app.totalTokens;
        $[69] = t26;
      } else {
        t26 = $[69];
      }
      let t27;
      if ($[70] !== t26) {
        t27 = <span className="font-display text-3xl font-bold tracking-tight tabular-nums">{t26}</span>;
        $[70] = t26;
        $[71] = t27;
      } else {
        t27 = $[71];
      }
      let t28;
      if ($[72] !== t) {
        t28 = t("public.apps.grid.tokensProcessed");
        $[72] = t;
        $[73] = t28;
      } else {
        t28 = $[73];
      }
      let t29;
      if ($[74] !== t28) {
        t29 = <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t28}</span>;
        $[74] = t28;
        $[75] = t29;
      } else {
        t29 = $[75];
      }
      if ($[76] !== t27 || $[77] !== t29) {
        t5 = <div className="flex items-baseline justify-between">{t27}{t29}</div>;
        $[76] = t27;
        $[77] = t29;
        $[78] = t5;
      } else {
        t5 = $[78];
      }
      t14 = "h-2 w-full overflow-hidden rounded-full bg-muted";
      t13 = cn("h-full rounded-full transition-all duration-700 ease-out", toneStyles.bar);
      $[25] = app.meta.Icon;
      $[26] = app.meta.description;
      $[27] = app.meta.displayName;
      $[28] = app.meta.url;
      $[29] = app.rank;
      $[30] = app.source;
      $[31] = app.totalTokens;
      $[32] = t;
      $[33] = tone;
      $[34] = T0;
      $[35] = t10;
      $[36] = t11;
      $[37] = t12;
      $[38] = t13;
      $[39] = t14;
      $[40] = t4;
      $[41] = t5;
      $[42] = t7;
      $[43] = t8;
      $[44] = t9;
    } else {
      T0 = $[34];
      t10 = $[35];
      t11 = $[36];
      t12 = $[37];
      t13 = $[38];
      t14 = $[39];
      t4 = $[40];
      t5 = $[41];
      t7 = $[42];
      t8 = $[43];
      t9 = $[44];
    }
    const t15 = `${Math.max(2, widthPct)}%`;
    let t16;
    if ($[79] !== t15) {
      t16 = {
        width: t15
      };
      $[79] = t15;
      $[80] = t16;
    } else {
      t16 = $[80];
    }
    let t17;
    if ($[81] !== t13 || $[82] !== t16) {
      t17 = <div className={t13} style={t16} />;
      $[81] = t13;
      $[82] = t16;
      $[83] = t17;
    } else {
      t17 = $[83];
    }
    if ($[84] !== t14 || $[85] !== t17) {
      t6 = <div className={t14}>{t17}</div>;
      $[84] = t14;
      $[85] = t17;
      $[86] = t6;
    } else {
      t6 = $[86];
    }
    t1 = "flex items-center justify-between text-xs text-muted-foreground";
    let t18;
    if ($[87] !== app.totalRequests) {
      t18 = numberFormatter.format(app.totalRequests);
      $[87] = app.totalRequests;
      $[88] = t18;
    } else {
      t18 = $[88];
    }
    let t19;
    if ($[89] !== t) {
      t19 = t("public.common.requests");
      $[89] = t;
      $[90] = t19;
    } else {
      t19 = $[90];
    }
    if ($[91] !== t18 || $[92] !== t19) {
      t2 = <span>{t18}{" "}{t19}</span>;
      $[91] = t18;
      $[92] = t19;
      $[93] = t2;
    } else {
      t2 = $[93];
    }
    t3 = lastUsed && <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />{t("public.apps.grid.lastSeenPrefix", {
        time: lastUsed
      })}</span>;
    $[0] = app.lastUsedAt;
    $[1] = app.meta.Icon;
    $[2] = app.meta.description;
    $[3] = app.meta.displayName;
    $[4] = app.meta.url;
    $[5] = app.rank;
    $[6] = app.source;
    $[7] = app.totalRequests;
    $[8] = app.totalTokens;
    $[9] = t;
    $[10] = tone;
    $[11] = widthPct;
    $[12] = T0;
    $[13] = t1;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[12];
    t1 = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t13;
  if ($[94] !== t1 || $[95] !== t2 || $[96] !== t3) {
    t13 = <div className={t1}>{t2}{t3}</div>;
    $[94] = t1;
    $[95] = t2;
    $[96] = t3;
    $[97] = t13;
  } else {
    t13 = $[97];
  }
  let t14;
  if ($[98] !== t13 || $[99] !== t4 || $[100] !== t5 || $[101] !== t6) {
    t14 = <div className={t4}>{t5}{t6}{t13}</div>;
    $[98] = t13;
    $[99] = t4;
    $[100] = t5;
    $[101] = t6;
    $[102] = t14;
  } else {
    t14 = $[102];
  }
  let t15;
  if ($[103] !== t14 || $[104] !== t7 || $[105] !== t8 || $[106] !== t9) {
    t15 = <div className={t7}>{t8}{t9}{t14}</div>;
    $[103] = t14;
    $[104] = t7;
    $[105] = t8;
    $[106] = t9;
    $[107] = t15;
  } else {
    t15 = $[107];
  }
  let t16;
  if ($[108] !== T0 || $[109] !== t10 || $[110] !== t11 || $[111] !== t12 || $[112] !== t15) {
    t16 = <T0 {...t10} className={t11}>{t12}{t15}</T0>;
    $[108] = T0;
    $[109] = t10;
    $[110] = t11;
    $[111] = t12;
    $[112] = t15;
    $[113] = t16;
  } else {
    t16 = $[113];
  }
  return t16;
}
function GridCard(t0) {
  const $ = _c(59);
  const {
    app,
    maxTokens,
    t
  } = t0;
  const widthPct = maxTokens > 0 ? app.totalTokens / maxTokens * 100 : 0;
  let t1;
  if ($[0] !== app.lastUsedAt || $[1] !== t) {
    t1 = formatRelative(app.lastUsedAt, t);
    $[0] = app.lastUsedAt;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const lastUsed = t1;
  const Wrap = app.meta.url ? "a" : "div";
  let t2;
  if ($[3] !== app.meta.url) {
    t2 = app.meta.url ? {
      href: app.meta.url,
      target: "_blank" as const,
      rel: "noopener noreferrer"
    } : {};
    $[3] = app.meta.url;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const wrapProps = t2;
  let t3;
  if ($[5] !== app.meta.Icon || $[6] !== app.meta.displayName) {
    t3 = <AppLogo Icon={app.meta.Icon} name={app.meta.displayName} />;
    $[5] = app.meta.Icon;
    $[6] = app.meta.displayName;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== app.meta.displayName) {
    t4 = <h3 className="font-display text-base font-semibold tracking-tight truncate">{app.meta.displayName}</h3>;
    $[8] = app.meta.displayName;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== app.source) {
    t5 = <p className="font-mono text-[11px] text-muted-foreground truncate">{app.source}</p>;
    $[10] = app.source;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="min-w-0">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t6) {
    t7 = <div className="flex min-w-0 items-center gap-3">{t3}{t6}</div>;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = String(app.rank);
  let t9;
  if ($[18] !== t8) {
    t9 = t8.padStart(2, "0");
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <span className="font-display text-2xl font-bold leading-none tracking-tighter tabular-nums text-muted-foreground/60">{t9}</span>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t7) {
    t11 = <div className="flex items-start justify-between gap-3">{t7}{t10}</div>;
    $[22] = t10;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== app.totalTokens) {
    t12 = formatTokens(app.totalTokens);
    $[25] = app.totalTokens;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <span className="font-display text-2xl font-bold tracking-tight tabular-nums">{t12}</span>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("public.apps.grid.tokensShort");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t14}</span>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t13 || $[34] !== t15) {
    t16 = <div className="flex items-baseline justify-between">{t13}{t15}</div>;
    $[33] = t13;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = `${Math.max(2, widthPct)}%`;
  let t18;
  if ($[36] !== t17) {
    t18 = <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-foreground/80 transition-all duration-700" style={{
        width: t17
      }} /></div>;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== app.totalRequests) {
    t19 = numberFormatter.format(app.totalRequests);
    $[38] = app.totalRequests;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("public.common.requests");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t19 || $[43] !== t20) {
    t21 = <span>{t19}{" "}{t20}</span>;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== lastUsed) {
    t22 = lastUsed && <span>{lastUsed}</span>;
    $[45] = lastUsed;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t21 || $[48] !== t22) {
    t23 = <div className="flex items-center justify-between text-[11px] text-muted-foreground">{t21}{t22}</div>;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t16 || $[51] !== t18 || $[52] !== t23) {
    t24 = <div className="mt-4 space-y-2">{t16}{t18}{t23}</div>;
    $[50] = t16;
    $[51] = t18;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== Wrap || $[55] !== t11 || $[56] !== t24 || $[57] !== wrapProps) {
    t25 = <Wrap {...wrapProps} className="group relative block rounded-xl border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-md">{t11}{t24}</Wrap>;
    $[54] = Wrap;
    $[55] = t11;
    $[56] = t24;
    $[57] = wrapProps;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
}
function ListRow(t0) {
  const $ = _c(45);
  const {
    app,
    maxTokens,
    t
  } = t0;
  const widthPct = maxTokens > 0 ? app.totalTokens / maxTokens * 100 : 0;
  let t1;
  if ($[0] !== app.lastUsedAt || $[1] !== t) {
    t1 = formatRelative(app.lastUsedAt, t);
    $[0] = app.lastUsedAt;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const lastUsed = t1;
  const Wrap = app.meta.url ? "a" : "div";
  let t2;
  if ($[3] !== app.meta.url) {
    t2 = app.meta.url ? {
      href: app.meta.url,
      target: "_blank" as const,
      rel: "noopener noreferrer"
    } : {};
    $[3] = app.meta.url;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const wrapProps = t2;
  const t3 = String(app.rank);
  let t4;
  if ($[5] !== t3) {
    t4 = t3.padStart(2, "0");
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <span className="font-display text-xl font-bold tabular-nums text-muted-foreground/70 tracking-tighter">{t4}</span>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== app.meta.Icon || $[10] !== app.meta.displayName) {
    t6 = <AppLogo Icon={app.meta.Icon} name={app.meta.displayName} size="sm" />;
    $[9] = app.meta.Icon;
    $[10] = app.meta.displayName;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== app.meta.displayName) {
    t7 = <p className="font-display text-sm font-semibold truncate">{app.meta.displayName}</p>;
    $[12] = app.meta.displayName;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== app.source) {
    t8 = <p className="font-mono text-[10px] text-muted-foreground truncate">{app.source}</p>;
    $[14] = app.source;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <div className="min-w-0">{t7}{t8}</div>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t6 || $[20] !== t9) {
    t10 = <div className="flex min-w-0 items-center gap-3">{t6}{t9}</div>;
    $[19] = t6;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== app.totalTokens) {
    t11 = formatTokens(app.totalTokens);
    $[22] = app.totalTokens;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <span className="font-display text-base font-semibold tabular-nums whitespace-nowrap">{t11}</span>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = `${Math.max(2, widthPct)}%`;
  let t14;
  if ($[26] !== t13) {
    t14 = <div className="h-1 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-foreground/70" style={{
        width: t13
      }} /></div>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t12 || $[29] !== t14) {
    t15 = <div className="hidden items-center gap-3 sm:flex">{t12}{t14}</div>;
    $[28] = t12;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== app.totalRequests) {
    t16 = numberFormatter.format(app.totalRequests);
    $[31] = app.totalRequests;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16) {
    t17 = <div className="hidden text-right text-xs text-muted-foreground tabular-nums sm:block">{t16}</div>;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const t18 = lastUsed ?? "\u2014";
  let t19;
  if ($[35] !== t18) {
    t19 = <div className="text-right text-xs text-muted-foreground">{t18}</div>;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== Wrap || $[38] !== t10 || $[39] !== t15 || $[40] !== t17 || $[41] !== t19 || $[42] !== t5 || $[43] !== wrapProps) {
    t20 = <Wrap {...wrapProps} className="group grid grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.5fr)_auto] items-center gap-4 border-b border-border/60 px-2 py-4 transition-colors hover:bg-muted/40 last:border-b-0 sm:grid-cols-[3rem_minmax(0,1fr)_minmax(0,2fr)_8rem_5rem]">{t5}{t10}{t15}{t17}{t19}</Wrap>;
    $[37] = Wrap;
    $[38] = t10;
    $[39] = t15;
    $[40] = t17;
    $[41] = t19;
    $[42] = t5;
    $[43] = wrapProps;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  return t20;
}
export function AppsGrid(t0) {
  const $ = _c(32);
  const {
    apps
  } = t0;
  const {
    t
  } = useI18n();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  let t1;
  if ($[0] !== apps) {
    t1 = apps.map(_temp);
    $[0] = apps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ranked = t1;
  let t2;
  if ($[2] !== category || $[3] !== query || $[4] !== ranked) {
    const needle = query.trim().toLowerCase();
    t2 = ranked.filter(a_0 => {
      if (category !== "all" && a_0.meta.category !== category) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return a_0.source.toLowerCase().includes(needle) || a_0.meta.displayName.toLowerCase().includes(needle);
    });
    $[2] = category;
    $[3] = query;
    $[4] = ranked;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const filtered = t2;
  const maxTokens = ranked[0]?.totalTokens ?? 0;
  const isFiltered = filtered.length !== ranked.length;
  let t3;
  if ($[6] !== filtered || $[7] !== isFiltered) {
    t3 = isFiltered ? [] : filtered.slice(0, 3);
    $[6] = filtered;
    $[7] = isFiltered;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const podium = t3;
  let t4;
  if ($[9] !== category || $[10] !== filtered || $[11] !== isFiltered || $[12] !== maxTokens || $[13] !== podium || $[14] !== query || $[15] !== t) {
    const gridApps = isFiltered ? filtered.slice(0, 12) : filtered.slice(3, 12);
    const listApps = isFiltered ? filtered.slice(12) : filtered.slice(12);
    let t5;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    let t6;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = e => setQuery(e.target.value);
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== t) {
      t7 = t("public.apps.grid.searchPlaceholder");
      $[19] = t;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== query || $[22] !== t7) {
      t8 = <div className="relative flex-1 max-w-md">{t5}<input type="text" value={query} onChange={t6} placeholder={t7} className="h-11 w-full rounded-full border bg-background pl-10 pr-4 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/40 focus:ring-2 focus:ring-foreground/10" /></div>;
      $[21] = query;
      $[22] = t7;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    let t9;
    if ($[24] !== category || $[25] !== t) {
      t9 = CATEGORIES.map(c => <button key={c.value} type="button" onClick={() => setCategory(c.value)} className={cn("rounded-full border px-4 py-1.5 text-sm transition-all", category === c.value ? "border-foreground bg-foreground text-background" : "border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground")}>{t(c.labelKey)}</button>);
      $[24] = category;
      $[25] = t;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    let t10;
    if ($[27] !== t9) {
      t10 = <div className="flex flex-wrap items-center gap-2">{t9}</div>;
      $[27] = t9;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    let t11;
    if ($[29] !== t10 || $[30] !== t8) {
      t11 = <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">{t8}{t10}</div>;
      $[29] = t10;
      $[30] = t8;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    t4 = <div className="space-y-12">{t11}{filtered.length === 0 ? <div className="rounded-2xl border border-dashed py-24 text-center"><p className="font-display text-lg font-semibold">{t("public.apps.grid.noResultsTitle")}</p><p className="mt-1 text-sm text-muted-foreground">{t("public.apps.grid.noResultsDescription")}</p></div> : <>{podium.length === 3 && <section className="space-y-4"><div className="flex items-baseline justify-between"><h2 className="font-display text-2xl font-bold tracking-tight">{t("public.apps.grid.leaderboard")}</h2><span className="text-xs uppercase tracking-widest text-muted-foreground">{t("public.apps.grid.topThree")}</span></div><div className="grid gap-5 lg:grid-cols-12"><div className="lg:col-span-6"><PodiumCard app={podium[0]} maxTokens={maxTokens} tone="gold" t={t} /></div><div className="lg:col-span-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-1"><PodiumCard app={podium[1]} maxTokens={maxTokens} tone="silver" t={t} /><PodiumCard app={podium[2]} maxTokens={maxTokens} tone="bronze" t={t} /></div></div></section>}{gridApps.length > 0 && <section className="space-y-4"><h2 className="font-display text-xl font-semibold tracking-tight text-muted-foreground">{isFiltered ? t("public.apps.grid.results") : t("public.apps.grid.moreApps")}</h2><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{gridApps.map(app => <GridCard key={app.source} app={app} maxTokens={maxTokens} t={t} />)}</div></section>}{listApps.length > 0 && <section className="space-y-4"><h2 className="font-display text-xl font-semibold tracking-tight text-muted-foreground">{t("public.apps.grid.longTail")}</h2><div className="rounded-2xl border bg-card overflow-hidden"><div className="hidden grid-cols-[3rem_minmax(0,1fr)_minmax(0,2fr)_8rem_5rem] gap-4 border-b bg-muted/30 px-2 py-3 text-[10px] font-medium uppercase tracking-widest text-muted-foreground sm:grid"><span>#</span><span>{t("public.apps.grid.app")}</span><span>{t("public.apps.grid.tokens")}</span><span className="text-right">{t("public.apps.grid.requests")}</span><span className="text-right">{t("public.apps.grid.lastSeen")}</span></div>{listApps.map(app_0 => <ListRow key={app_0.source} app={app_0} maxTokens={maxTokens} t={t} />)}</div></section>}</>}</div>;
    $[9] = category;
    $[10] = filtered;
    $[11] = isFiltered;
    $[12] = maxTokens;
    $[13] = podium;
    $[14] = query;
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
function _temp(a, idx) {
  return {
    ...a,
    rank: idx + 1,
    meta: getAppMetadata(a.source)
  };
}